'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/lib/auth-context';

interface OutcomeFormProps {
  decisionId: string;
  decisionTitle: string;
  existingOutcome?: string | null;
  existingRating?: number | null;
  existingOutcomeDate?: string | null;
}

export default function OutcomeForm({
  decisionId,
  decisionTitle,
  existingOutcome,
  existingRating,
  existingOutcomeDate,
}: OutcomeFormProps) {
  const router = useRouter();
  const { permissions, isLoading } = useAuth();
  const hasExistingOutcome = existingOutcome != null || existingRating != null;
  const [outcome, setOutcome] = useState(existingOutcome || '');
  const [rating, setRating] = useState<number | null>(existingRating ?? null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`/api/decisions/${decisionId}/outcome`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          outcome: outcome.trim() || null,
          outcomeRating: rating,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update outcome');
      }

      setShowForm(false);
      router.refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseWithoutOutcome = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`/api/decisions/${decisionId}/outcome`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          outcome: 'Closed without detailed outcome',
          outcomeRating: null,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to close decision');
      }

      setShowForm(false);
      router.refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!showForm) {
    // Show existing outcome with Update button
    if (hasExistingOutcome) {
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">
              {'★'.repeat(existingRating || 0)}
              {'☆'.repeat(5 - (existingRating || 0))}
            </span>
            <span className="text-zinc-600 dark:text-zinc-400">
              ({existingRating}/5)
            </span>
          </div>
          {existingOutcome && (
            <p className="text-zinc-900 dark:text-zinc-100 whitespace-pre-wrap">
              {existingOutcome}
            </p>
          )}
          {existingOutcomeDate && (
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              Recorded on {new Date(existingOutcomeDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          )}
          {!isLoading && permissions.canReflect && (
            <div className="flex justify-end">
              <button
                onClick={() => setShowForm(true)}
                className="px-4 py-2 bg-transparent border border-gray-300 dark:border-gray-600 text-gray-300 dark:text-gray-600
                           rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              >
                Update Outcome
              </button>
            </div>
          )}
        </div>
      );
    }

    // No outcome yet - show form trigger
    return (
      <div className="space-y-3">
        <p className="text-yellow-800 dark:text-yellow-200">
          No outcome recorded yet.
        </p>
        {!isLoading && permissions.canReflect && (
          <div className="flex flex-wrap justify-between items-center">
            <button
              onClick={() => setShowForm(true)}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
            >
              Rate Outcome
            </button>
            <button
              onClick={handleCloseWithoutOutcome}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Closing...' : 'Close Without Outcome'}
            </button>
          </div>
        )}
        {error && (
          <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="rating" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Outcome Rating
        </label>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          This is all Mini-Me learns from. A rating on its own is a complete outcome — the notes below are optional.
        </p>
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              className={`text-2xl transition-colors ${
                rating !== null && star <= rating
                  ? 'text-yellow-500'
                  : 'text-gray-300 dark:text-gray-600'
              } hover:text-yellow-400`}
            >
              ★
            </button>
          ))}
          {rating !== null && (
            <button
              type="button"
              onClick={() => setRating(null)}
              className="text-sm text-gray-500 hover:text-gray-700 ml-2"
            >
              Clear
            </button>
          )}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          1 = Very poor | 3 = Neutral | 5 = Excellent
        </div>
      </div>

      <div>
        <label htmlFor="outcome" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          What happened? <span className="font-normal text-gray-400 dark:text-gray-500">(optional)</span>
        </label>
        <textarea
          id="outcome"
          value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
          rows={4}
          placeholder="Optional — leave blank if the rating says enough."
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                     bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                     focus:ring-2 focus:ring-blue-500 focus:border-transparent
                     placeholder-gray-400 dark:placeholder-gray-500"
        />
      </div>

      {error && (
        <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
      )}

      <div className="flex gap-3 justify-end">
        <button
          type="button"
          onClick={() => setShowForm(false)}
          className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300
                     rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting || (rating === null && outcome.trim() === '')}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700
                     transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Saving...' : 'Save Outcome'}
        </button>
      </div>
    </form>
  );
}
